<!-- Informational Alert -->
<div *ngIf="isDisabled" class="alert alert-info" role="alert">
  <i class="bi bi-info-circle me-2"></i>
  {{ infoMessage }}
</div>

<!-- Error Message -->
<div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
  <i class="bi bi-exclamation-triangle-fill me-2"></i>
  {{ errorMessage }}
  <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''" aria-label="Close"></button>
</div>

<!-- Success Message -->
<div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
  <i class="bi bi-check-circle-fill me-2"></i>
  {{ successMessage }}
  <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''" aria-label="Close"></button>
</div>

<!-- Loading -->
<div *ngIf="loading" class="text-center py-4">
  <div class="spinner-border spinner-border-sm text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
  <span class="ms-2 text-muted">Loading locations...</span>
</div>

<!-- Locations Content -->
<div *ngIf="!loading">
  <!-- Header with Add button -->
  <div class="d-flex align-items-center justify-content-between mb-3">
    <h6 class="mb-0">
      <i class="bi bi-geo-alt me-2"></i>
      Locations
      <span class="badge bg-secondary ms-2">{{ locations.length }}</span>
    </h6>
    <button
      class="btn btn-primary btn-sm"
      (click)="toggleAddForm()"
      [disabled]="isDisabled"
    >
      <i class="bi" [class.bi-plus]="!showAddForm" [class.bi-x]="showAddForm"></i>
      {{ showAddForm ? 'Cancel' : 'Add Location' }}
    </button>
  </div>

  <!-- Add Location Form -->
  <div *ngIf="showAddForm" class="card mb-3">
    <div class="card-body">
      <h6 class="mb-3">New Location</h6>
      <div *ngIf="addFormError" class="alert alert-danger py-2 mb-3">
        {{ addFormError }}
      </div>
      <div class="row g-3">
        <div class="col-md-6">
          <label class="form-label">Name <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="newLocation.name"
            placeholder="e.g., Des Moines Office"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label">Street 1 <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="newLocation.street1"
            placeholder="Street address"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label">Street 2</label>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="newLocation.street2"
            placeholder="Suite, unit, etc. (optional)"
          />
        </div>
        <div class="col-md-3">
          <label class="form-label">City <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="newLocation.city"
            placeholder="City"
          />
        </div>
        <div class="col-md-2">
          <label for="newLocationState" class="form-label">State <span class="text-danger">*</span></label>
          <app-us-state-select
            inputId="newLocationState"
            display="code"
            placeholder="Select..."
            [(ngModel)]="newLocation.state">
          </app-us-state-select>
        </div>
        <div class="col-md-3">
          <label class="form-label">Zip Code <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="newLocation.zipCode"
            placeholder="Zip code"
          />
        </div>
        <div class="col-md-3">
          <label class="form-label">Phone</label>
          <input
            type="tel"
            class="form-control"
            [(ngModel)]="newLocation.phone"
            mask="(000) 000-0000"
            [validation]="false"
            placeholder="(optional)"
          />
        </div>
        <div class="col-md-3">
          <label class="form-label">Email</label>
          <input
            type="email"
            class="form-control"
            [(ngModel)]="newLocation.email"
            placeholder="(optional)"
          />
        </div>
        <div class="col-md-3">
          <label class="form-label">Latitude</label>
          <input
            type="number"
            class="form-control"
            [(ngModel)]="newLocation.latitude"
            placeholder="(optional)"
            step="0.0000001"
          />
        </div>
        <div class="col-md-3">
          <label class="form-label">Longitude</label>
          <input
            type="number"
            class="form-control"
            [(ngModel)]="newLocation.longitude"
            placeholder="(optional)"
            step="0.0000001"
          />
        </div>
      </div>
      <div class="mt-3">
        <button
          class="btn btn-primary btn-sm"
          (click)="submitLocation()"
          [disabled]="addingLocation"
        >
          <span *ngIf="addingLocation" class="spinner-border spinner-border-sm me-1" role="status"></span>
          {{ addingLocation ? 'Creating...' : 'Create Location' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <div *ngIf="locations.length === 0 && !showAddForm" class="text-center py-5 text-muted">
    <i class="bi bi-geo-alt fs-1 d-block mb-3"></i>
    <p>No locations configured for this sponsor.</p>
    <button
      class="btn btn-outline-primary btn-sm"
      (click)="toggleAddForm()"
      [disabled]="isDisabled"
    >
      <i class="bi bi-plus me-1"></i>
      Add First Location
    </button>
  </div>

  <!-- Locations Table -->
  <div class="table-responsive" *ngIf="locations.length > 0">
    <table class="table table-hover mb-0">
      <thead>
        <tr>
          <th>Name</th>
          <th>Address</th>
          <th>Phone</th>
          <th>Email</th>
          <th class="text-end">Actions</th>
        </tr>
      </thead>
      <tbody>
        <ng-container *ngFor="let location of locations">
          <!-- Display Row -->
          <tr *ngIf="editingLocationId !== location.id">
            <td>{{ location.name }}</td>
            <td>{{ formatAddress(location) }}</td>
            <td>{{ (location.phone | phoneFormat) || '---' }}</td>
            <td>{{ location.email || '---' }}</td>
            <td class="text-end">
              <div class="d-flex gap-2 justify-content-end">
                <button
                  class="btn btn-outline-primary btn-sm"
                  (click)="startEdit(location)"
                  title="Edit location"
                  [disabled]="isDisabled"
                >
                  <i class="bi bi-pencil"></i>
                </button>
                <button
                  class="btn btn-outline-danger btn-sm"
                  (click)="archiveLocation(location)"
                  title="Archive location"
                  [disabled]="isDisabled"
                >
                  <i class="bi bi-archive"></i>
                </button>
              </div>
            </td>
          </tr>

          <!-- Edit Row -->
          <tr *ngIf="editingLocationId === location.id" class="table-active">
            <td colspan="5">
              <div class="p-2">
                <h6 class="mb-3">Edit Location</h6>
                <div *ngIf="editError" class="alert alert-danger py-2 mb-3">
                  {{ editError }}
                </div>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">Name <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" [(ngModel)]="editData.name" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Street 1 <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" [(ngModel)]="editData.street1" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Street 2</label>
                    <input type="text" class="form-control" [(ngModel)]="editData.street2" />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label">City <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" [(ngModel)]="editData.city" />
                  </div>
                  <div class="col-md-2">
                    <label for="editLocationState" class="form-label">State <span class="text-danger">*</span></label>
                    <app-us-state-select
                      inputId="editLocationState"
                      display="code"
                      placeholder="Select..."
                      [(ngModel)]="editData.state">
                    </app-us-state-select>
                  </div>
                  <div class="col-md-3">
                    <label class="form-label">Zip Code <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" [(ngModel)]="editData.zipCode" />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label">Phone</label>
                    <input type="tel" class="form-control" [(ngModel)]="editData.phone" mask="(000) 000-0000" [validation]="false" />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label">Email</label>
                    <input type="email" class="form-control" [(ngModel)]="editData.email" />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label">Latitude</label>
                    <input type="number" class="form-control" [(ngModel)]="editData.latitude" step="0.0000001" />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label">Longitude</label>
                    <input type="number" class="form-control" [(ngModel)]="editData.longitude" step="0.0000001" />
                  </div>
                </div>
                <div class="mt-3 d-flex gap-2">
                  <button
                    class="btn btn-primary btn-sm"
                    (click)="saveEdit()"
                    [disabled]="savingEdit"
                  >
                    <span *ngIf="savingEdit" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    {{ savingEdit ? 'Saving...' : 'Save' }}
                  </button>
                  <button class="btn btn-outline-secondary btn-sm" (click)="cancelEdit()">Cancel</button>
                </div>
              </div>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>
  </div>
</div>
